<template>
  <div class="screen">
    <div class="text-center" v-if="isBroadcasting">
      <p class="title">
        Present your presentation as normal from Keynote or Powerpoint.
      </p>
      <p class="body-1">
        Ready to broadcast your presentation&hellip;
      </p>
      <v-btn text color="primary" @click.native="cancelPresentation">
        Stop
      </v-btn>
    </div>

    <div v-if="currentDraft && !isBroadcasting" class="px-3">
      <h1 class="display-1">{{currentDraft.title}}</h1>

      <v-subheader>Next steps</v-subheader>
      <v-row>
        <v-col cols="4">
          <v-card class="pa-3 mx-3">
            <p class="body-1">
              <span class="body-2">Step 1</span><br>
              Put the checkin ID clearly visible on your title slide.<br>
              We recommend to add the ID also to subsequent slides
              because your audience will realise later they want to capture your content.
            </p>
            <canvas id="checkin-id-canvas" width="192" height="100"></canvas>
            <v-btn :href="svgDownloadUrl" :download="'at-and-' + currentDraft.checkinId">
              Save image
            </v-btn>
          </v-card>
        </v-col>

        <v-col cols="4">
          <v-card class="pa-3 mx-3">
            <p>
              <span class="body-2">Step 2</span><br>When your presentation is final-final,<br>
              export it as images,<br>
              and upload them here all at once.</p>
            <div v-if="currentDraftSlides" class="mb-3">
              Uploaded: {{currentDraftSlides.length}} slides
              <v-btn
                  text
                  small
                  icon
                  color="error"
                  @click="deleteImages()">
                <v-icon>clear</v-icon>
              </v-btn>
            </div>
            <v-btn @click.native="selectFile"
                   v-if="!currentDraftSlides">
              Upload all slides
            </v-btn>
            <input
                id="files"
                type="file"
                name="file"
                ref="uploadInput"
                accept="image/*"
                :multiple="true"
                @change="detectFiles($event)">
            <v-progress-circular
                v-if="totalProgress > 0 && !uploadEnd"
                :size="100"
                :width="15"
                :rotate="360"
                :value="totalProgress"
                color="primary">
              {{ totalProgress }}%
            </v-progress-circular>
          </v-card>
        </v-col>

        <v-col cols="4">
          <v-card class="pa-3 mx-3">
            <p>
              <span class="body-2">Step 3</span><br>
              Start your presentation!
            </p>
            <v-btn color="primary" @click="startPresentation()" :disabled="!currentDraftSlides">
              Start
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import firebaseApp from '../api/firebaseClient';
import desktopAppApi from '../api/desktopAppApi';

export default {
  name: 'ViewPresentation',

  beforeCreate() {
    this.$store.dispatch('setCurrentDraft', this.$route.params.presentationId);
  },

  mounted() {
    this.createCheckinIdImage();
  },

  data() {
    return {
      uploadList: [],
      progressList: [],
      totalProgress: 0,
      uploadTasks: 0,
      uploadEnd: false,
      svgDownloadUrl: ''
    };
  },

  computed: {
    ...mapGetters([
      'currentDraft',
      'currentDraftSlides',
      'isBroadcasting'
    ])
  },

  methods: {
    selectFile() {
      this.$refs.uploadInput.click();
    },

    detectFiles(e) {
      const fileList = e.target.files || e.dataTransfer.files;
      Array.from(Array(fileList.length).keys()).map(x => this.upload(fileList[x]));
    },

    upload(file) {
      this.uploadList.push({
        name: file.name
      });
      this.uploadTasks += 1;
      this.progressList.push(0);
      const uploadTask = firebaseApp.storage.ref(`${this.currentDraft.presentationId}/${file.name}`).put(file);
      uploadTask.on('state_changed', (snapshot) => {
        const indexOfUpload = this.uploadList.findIndex(
          upload => upload.name === snapshot.ref.name
        );
        this.progressList[indexOfUpload] = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        const totalBytes = this.progressList.length ? this.progressList.reduce((a, b) => a + b) : 0;
        this.totalProgress = Math.round(totalBytes / this.progressList.length);
      },
      null,
      () => {
        uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
          this.uploadTasks -= 1;
          const indexOfUpload = this.uploadList.findIndex(
            upload => upload.name === uploadTask.snapshot.ref.name
          );
          this.uploadList[indexOfUpload].image = downloadURL;

          if (this.uploadTasks === 0) {
            this.uploadEnd = true;
            this.addSlidesToPresentation(this.uploadList);
          }
        });
      });
    },

    deleteImages() {
      this.currentDraftSlides.forEach((slide) => {
        firebaseApp.storage
          .ref(`${this.currentDraft.presentationId}/${slide.name}`)
          .delete()
          .then(() => {
            console.log('Slide deleted');
          })
          .catch((error) => {
            console.error(`file delete error occured: ${error}`);
          });

        firebaseApp.storage
          .ref(`${this.currentDraft.presentationId}/thumb_${slide.name}`)
          .delete()
          .then(() => {
            console.log('Slide thumb deleted');
          })
          .catch((error) => {
            console.error(`file delete error occured: ${error}`);
          });
      });

      this.uploadList = [];
      this.addSlidesToPresentation(this.uploadList);
    },

    addSlidesToPresentation(slides) {
      this.$store.dispatch('storeSlides', {
        presentationId: this.currentDraft.presentationId,
        slides
      });
    },

    startPresentation() {
      this.$store.dispatch('startBroadcasting');
      desktopAppApi.listenToPresentationStatus(this.currentDraftSlides.length);
    },

    cancelPresentation() {
      this.$store.dispatch('stopBroadcasting');
      desktopAppApi.stopListenToPresentationStatus();
    },

    createCheckinIdImage() {
      const canvas = document.getElementById('checkin-id-canvas');
      const ctx = canvas.getContext('2d');
      ctx.font = '24px Roboto,sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(0,0,0,0.87)';
      ctx.fillText('At-And.com', 96, 30);
      ctx.font = '56px Roboto,sans-serif';
      ctx.fillText(this.currentDraft.checkinId, 96, 90);

      canvas.toBlob((blob) => {
        this.svgDownloadUrl = URL.createObjectURL(blob);
      });
    }
  }
};
</script>

<style>
  input[type="file"] {
    position: absolute;
    clip: rect(0,0,0,0);
  }
</style>



// WEBPACK FOOTER //
// src/pages/ViewPresentation.vue